import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Form from 'react-bootstrap/Form';
import { useNavigate, Link } from 'react-router-dom'
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import toast from 'react-hot-toast';
import DatePicker from "react-datepicker";
import InputGroup from 'react-bootstrap/InputGroup';
import { useTranslation } from 'react-i18next';
import { calculateMaxHeightContent, formatNumber, unformatNumber } from '../../../resources/functions';
import { OpennigBalanceFormI } from '../../../resources/form-props';
import { OpenningBalanceValidate } from '../../../resources/form-validator';
import { thirdPartyApi } from '../../../resources/hooks/api/thirdPartyApiHook';
import { useSettingsApi } from '../../../resources/hooks/api/useSettingsApiHook';
import { getBranch } from '../../../store/user/selectors'
import { URLS } from '../../../resources/constants';
import '../../../assets/css/datepicker.css'
import '../../OpenningBalance/openning-balance.css';
import { glClassificationApi } from '../../../resources/hooks/api/glClassificationApi';
import { getAccountSettings } from '../../../store/settings/selectors'
import SAlert from '../../../components/SAlert';

interface Props {

}

const QBBeginningBalanceClassification: React.FC<Props> = () => {
    const branchData = useSelector(getBranch)
    const { qbFetchOpenningAcc, postQuickBookBeginningBalance } = thirdPartyApi();
    const { fetchAccountSettingsData } = useSettingsApi();
    const navigate = useNavigate();
    const { t } = useTranslation("scenes.thirdparty.quickbook.BBClassification");

    const _getAccountSettings = useSelector(getAccountSettings);

    const calendarRef = useRef<any>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [totalAssets, setTotalAssets] = useState<number>(0);
    const [totalLiabilities, setTotalLiabilities] = useState<number>(0);
    const [isEdit, setIsEdit] = useState<boolean>(true);
    const [showConfirm, setShowConfirm] = useState<boolean>(false)
    const [dataLoaded, setDataLoaded] = useState<boolean>(false)
    // Last CLOSED month. Defaulting to the current month produced an end date in the
    // future (and an in-progress month has no settled closing balance to open from).
    // Matches the cut-off BeginningBalanceImportModal already enforces.
    const lastClosedMonthEnd = () => moment().subtract(1, 'month').endOf('month');

    const getInitialFilter = () => {
        return { endDate: lastClosedMonthEnd().format('YYYY-MM-DD') };
    };

    const [selectedFilter, setSelectedFilter] = useState<any>(getInitialFilter());
    const [contentMaxHeight, setContentMaxHeight] = useState<number>(0);

    const { register, control, setValue, getValues, reset, handleSubmit, formState: { errors } } = useForm<OpennigBalanceFormI>({
        defaultValues: {
            assets: [],
            liabilities: []
        },
        resolver: yupResolver(OpenningBalanceValidate),
    });
    const { fields: assetFields, append: addMoreAsset, remove: deleteAsset, replace: replaceAsset } = useFieldArray({
        control,
        name: 'assets',
    });
    const { fields: liabilitiesFields, append: addMoreLiabilities, remove: deleteLiabilities, replace: replaceLiabilities } = useFieldArray({
        control,
        name: 'liabilities',
    });

    useEffect(() => {
        setContentMaxHeight(calculateMaxHeightContent(78));
    })

    const resetForm = () => {
        setTotalAssets(0);
        setTotalLiabilities(0);

        reset({
            assets: [],
            liabilities: []
        });
    }

    const _handleChangeSearchParam = (e: any, type: string) => {
        if (moment(e).isValid()) {
            const newDate = moment(e).endOf('month').format('YYYY-MM-DD');
            setSelectedFilter({
                'endDate': newDate
            });
            localStorage.setItem('bbSelectedDate', JSON.stringify([newDate]));
        }
    }

    const handleAssetsInputChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        let inputValue = e.target.value.replace(/[^0-9.-]/g, '');
        const isNegative = inputValue.startsWith('-');

        // Allow input of negative numbers
        if (isNegative) {
            inputValue = inputValue.substring(1);
        }

        const formattedValue = formatNumber(inputValue);

        const result = isNegative ? `-${formattedValue}` : formattedValue;

        setValue(`assets.${index}.opening_balance`, result);
        calculateTotalAsset();
    };

    const handleLiabilitiesInputChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        let inputValue = e.target.value.replace(/[^0-9.-]/g, '');
        const isNegative = inputValue.startsWith('-');

        // Allow input of negative numbers
        if (isNegative) {
            inputValue = inputValue.substring(1);
        }

        const formattedValue = formatNumber(inputValue);

        const result = isNegative ? `-${formattedValue}` : formattedValue;

        setValue(`liabilities.${index}.opening_balance`, result);
        calculateTotalBSLiabilities();
    };

    const openCalendar = () => {
        if (calendarRef.current) {
            calendarRef.current?.setOpen(true)
        }
    }

    const computeAssetsSum = (items: any[]) => {
        let sum = 0;
        (items || []).forEach((item: any) => {
            if (['VP', 'IP', 'VP/IP'].includes(item.report_code)) {
                sum += unformatNumber(item.opening_balance) || 0;
            }
        });
        return parseFloat(sum.toFixed(2));
    };

    const computeLiabilitiesSum = (items: any[]) => {
        let sum = 0;
        (items || []).forEach((item: any) => {
            if (['VP', 'IP', 'VP/IP'].includes(item.report_code)) {
                sum += unformatNumber(item.opening_balance) || 0;
            }
        });
        return parseFloat(sum.toFixed(2));
    };

    // 51990 TOTAL LIABILITIES is liabilities-proper only (equity starts at 52000), unlike
    // 59990 which is the grand total including equity. Neither is stored server-side —
    // both are derived here, same as 39990 on the assets side.
    const computeLiabilitiesOnlySum = (items: any[]) => {
        let sum = 0;
        (items || []).forEach((item: any) => {
            if (['VP', 'IP', 'VP/IP'].includes(item.report_code) && +item.account < 52000) {
                sum += unformatNumber(item.opening_balance) || 0;
            }
        });
        return parseFloat(sum.toFixed(2));
    };

    const fetchList = (targetDate?: string) => {
        setTotalAssets(0);
        setTotalLiabilities(0);

        const dateVal = targetDate || selectedFilter['endDate'];
        localStorage.setItem('bbSelectedDate', JSON.stringify([dateVal]));

        const params = {
            branch_id: +branchData['id'],
            month: +moment(dateVal).format('M'),
            year: +moment(dateVal).format('Y')
        }
        setIsLoading(true);
        qbFetchOpenningAcc(params, (message: string, resp: any) => {
            setIsLoading(false);

            if (!resp.data.data.is_exists) {
                toast.error(t('toast.error.noQBOData'));
            }

            const assetsList = (resp.data.data.assets || []).map((data: any) => ({
                opening_balance: data['opening_balance'] ? formatNumber('' + data['opening_balance']) : '',
                account: data['account'],
                name: data['name'],
                description: data['description'],
                report_code: data['report_code'],
                financial_statement: data['financial_statement'],
                master_chart_of_account_id: data['master_chart_of_account_id'],
                credit: data['credit'] || '',
                debit: data['debit'] || '',
            }));

            const liabilitiesList = (resp.data.data.liabilities || []).map((data: any) => ({
                opening_balance: data['opening_balance'] ? formatNumber('' + data['opening_balance']) : '',
                account: data['account'],
                name: data['name'],
                description: data['description'],
                report_code: data['report_code'],
                financial_statement: data['financial_statement'],
                master_chart_of_account_id: data['master_chart_of_account_id'],
                credit: data['credit'] || '',
                debit: data['debit'] || '',
            }));

            const totAssets = computeAssetsSum(assetsList);
            const totLiab = computeLiabilitiesSum(liabilitiesList);

            const assetIndex = assetsList.findIndex((item: any) => item.account == 39990);
            if (assetIndex !== -1) {
                assetsList[assetIndex].opening_balance = '' + totAssets;
            }

            const liabIndex = liabilitiesList.findIndex((item: any) => item.account == 59990);
            if (liabIndex !== -1) {
                liabilitiesList[liabIndex].opening_balance = '' + totLiab;
            }

            const liabOnlyIndex = liabilitiesList.findIndex((item: any) => item.account == 51990);
            if (liabOnlyIndex !== -1) {
                liabilitiesList[liabOnlyIndex].opening_balance = '' + computeLiabilitiesOnlySum(liabilitiesList);
            }

            replaceAsset(assetsList);
            replaceLiabilities(liabilitiesList);
            setTotalAssets(totAssets);
            setTotalLiabilities(totLiab);
            setIsEdit(false);
            setDataLoaded(true);

            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 100)
        }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.error(message);
        })
    }

    const _handleSubmit = (data: any) => {
        const finalDate = selectedFilter['endDate'];
        localStorage.setItem('bbSelectedDate', JSON.stringify([finalDate]));

        const queryString = {
            branch_id: +branchData['id'],
            month: +moment(finalDate).format('M'),
            year: +moment(finalDate).format('Y')
        }
        const formData = {
            "opening_balance": [
                ...data.assets.filter((e: any) => e['opening_balance'] != 0).map((e: any) => ({
                    ...e,
                    opening_balance: unformatNumber(e['opening_balance'])
                })),
                ...data.liabilities.filter((e: any) => e['opening_balance'] != 0).map((e: any) => ({
                    ...e,
                    opening_balance: unformatNumber(e['opening_balance'])
                })),
            ],
            "total_assets": totalAssets,
            "total_liabilities": totalLiabilities
        }

        if (Math.abs(totalAssets - totalLiabilities) > 0.01) {
            toast.error(t("toast.error.assetsLiabilitiesNotEqual"));
            return;
        }

        setIsLoading(true)
        postQuickBookBeginningBalance({ queryString, formData }, (message: string, resp: any) => {
            setIsLoading(false)
            toast.success(message);
            navigate(-1);
        }, (message: string, resp: any) => {
            setIsLoading(false)
            toast.error(message);
        })
    }

    const submitForm = () => {
        setShowConfirm(true);
        // handleSubmit(_handleSubmit)();
    };

    const calculateTotalAsset = () => {
        const tot = computeAssetsSum(getValues('assets'));
        const gotIndex = getValues('assets').findIndex((item: any) => item.account == 39990);
        setTotalAssets(tot);
        if (gotIndex !== -1) {
            setValue(`assets.${gotIndex}.opening_balance`, '' + tot);
        }
    }
    const calculateTotalBSLiabilities = () => {
        const tot = computeLiabilitiesSum(getValues('liabilities'));
        const gotIndex = getValues('liabilities').findIndex((item: any) => item.account == 59990);
        setTotalLiabilities(tot);
        if (gotIndex !== -1) {
            setValue(`liabilities.${gotIndex}.opening_balance`, '' + tot);
        }
        const liabOnlyIndex = getValues('liabilities').findIndex((item: any) => item.account == 51990);
        if (liabOnlyIndex !== -1) {
            setValue(`liabilities.${liabOnlyIndex}.opening_balance`, '' + computeLiabilitiesOnlySum(getValues('liabilities')));
        }
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
        if (event.key === 'Enter' || event.keyCode === 13) {
            event.preventDefault();
        }
    };

    return (
        <div className="main-container flex-grow-1">
            <div className="container-fluid">
                <div className="page-title pb-3 pt-3">
                    <div className="row align-items-center">
                        <div className="col-sm-6 align-items-center d-flex">
                            <h1 className="h5 font-weight-700 mb-0 d-inline-flex">{t("title.page")}</h1>
                        </div>
                    </div>
                </div>
                <div className="executive-dashboard">
                    <div className={`page-loader ${isLoading ? 'visible' : ''}`}>
                        <div className="loader"></div>
                    </div>
                    <div className="row">
                        <div className="align-items-end col-md-7 d-flex gap-2 justify-content-end">
                            <div className="filter-box form-group date-picker-container mb-2 position-relative" style={{ maxWidth: '240px' }}>
                                <label htmlFor="" className="form-label font-weight-700 text-dark mb-1">
                                    {t("label.endingDate")}<strong className='text-danger'>*</strong>
                                </label>
                                <div className="position-relative">
                                    <DatePicker
                                        ref={calendarRef}
                                        selected={selectedFilter['endDate'] && moment(selectedFilter['endDate']).isValid() ? moment(selectedFilter['endDate']).toDate() : null}
                                        onChange={(date: Date) => _handleChangeSearchParam(date, 'endDate')}
                                        dateFormat={'MM/dd/yyyy'}
                                        maxDate={lastClosedMonthEnd().toDate()}
                                        showMonthYearPicker
                                        placeholderText={t("placeholder.selectMonthYear")}
                                        disabled={isLoading}
                                        customInput={
                                            <input
                                                type="text"
                                                className="form-control font-weight-600 text-dark bg-white shadow-sm cursor-pointer"
                                                placeholder={t("placeholder.selectMonthYear")}
                                                style={{
                                                    height: '42px',
                                                    borderRadius: '10px',
                                                    borderColor: '#6366f1',
                                                    borderWidth: '1.5px',
                                                    backgroundColor: '#ffffff',
                                                    color: '#0f172a',
                                                    fontSize: '14px',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    paddingRight: '36px'
                                                }}
                                            />
                                        }
                                    />
                                    <i
                                        className="fa fa-calendar fa-lg text-primary cursor-pointer"
                                        aria-hidden="true"
                                        onClick={openCalendar}
                                        style={{
                                            position: 'absolute',
                                            right: '12px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            zIndex: 5,
                                            color: '#6366f1'
                                        }}
                                    ></i>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn btn-primary mb-2 d-inline-flex align-items-center gap-1 shadow-sm font-weight-600"
                                style={{
                                    height: '42px',
                                    borderRadius: '10px',
                                    paddingLeft: '20px',
                                    paddingRight: '20px',
                                    fontSize: '14px'
                                }}
                                onClick={() => fetchList(selectedFilter['endDate'])}
                                disabled={isLoading || !selectedFilter['endDate']}
                            >
                                {isLoading ? (
                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                ) : (
                                    <i className="fas fa-check-circle me-1"></i>
                                )}
                                <span>{t("button.apply")}</span>
                            </button>
                        </div>
                    </div>

                    {!dataLoaded ? (
                        <div className="card shadow-sm border-0 text-center py-5 my-4" style={{ borderRadius: '14px', backgroundColor: '#f8fafc' }}>
                            <div className="card-body">
                                <i className="fas fa-calendar-alt text-primary fa-3x mb-3"></i>
                                <h4 className="font-weight-700 text-dark">{t("emptyState.title")}</h4>
                                <p className="text-muted mb-0" style={{ maxWidth: '520px', margin: '0 auto', fontSize: '15px' }}>
                                    {t("emptyState.description")}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div style={{ padding: 0, margin: 0, textAlign: 'center' }}>
                            <div className='col-md-12 d-flex justify-content-center'>
                            <Form className='w-100' onKeyDown={handleKeyDown} /*onSubmit={handleSubmit(saveMonthlyIncome)}*/>
                            <div>
                                <table className="openning-balance table boder-padding-none">
                                    <tr>
                                        <td colSpan={12}>
                                            <table className="section-table">
                                                <tr className="header">
                                                    <td width={'10%'}>{t("table.assets.header.account")}</td>
                                                    <td width={'60%'}>{t("table.assets.header.name")}</td>
                                                    <td width={'40%'}>{t("table.assets.header.amount")}</td>
                                                </tr>
                                                {assetFields.map((item: any, index: number) => (
                                                    <tr key={item.id} className={`${['GH', 'SH', "SSH"].includes(item.report_code) ? 'grey-background' : ''} ${['GT', 'ST', 'SST'].includes(item.report_code) ? 'total-row' : ''}`}>
                                                        <td className='text-start'>
                                                            <label htmlFor="">
                                                                {
                                                                    ['VP', 'IP', "VP/IP"].includes(item.report_code) ?
                                                                        getValues(`assets.${index}.account`) : getValues(`assets.${index}.account`) == 39990 ? (<span style={{ fontSize: 'larger' }}>{getValues(`assets.${index}.account`)}</span>) : ['GT', 'ST', 'SST'].includes(item.report_code) ? (
                                                                            <span></span>
                                                                        ) : <span>{getValues(`assets.${index}.account`)}</span>
                                                                }
                                                            </label>
                                                        </td>
                                                        <td className='text-start'>
                                                            {
                                                                ['VP', 'IP', "VP/IP"].includes(item.report_code) ?
                                                                    <input
                                                                        type='text'
                                                                        {...register(`assets.${index}.name`)}
                                                                        placeholder={t("table.assets.input.name.placeholder")}
                                                                        readOnly={!isEdit}
                                                                        className={`form-control ${errors && errors.assets && errors.assets[index]?.name ? 'is-invalid' : ''} ${isEdit ? '' : 'input-disabled-look'}`}
                                                                    /> : <label htmlFor="">
                                                                        {
                                                                            getValues(`assets.${index}.account`) == 39990 ?
                                                                                <span style={{ fontSize: 'larger' }}>
                                                                                    {getValues(`assets.${index}.name`)}
                                                                                </span> :
                                                                                <span>
                                                                                    {getValues(`assets.${index}.name`)}
                                                                                </span>
                                                                        }
                                                                    </label>
                                                            }
                                                        </td>
                                                        {
                                                            ['VP', 'IP', "VP/IP"].includes(item.report_code) ? (
                                                                <td>
                                                                    <InputGroup hasValidation className="input-group-custom">
                                                                        <InputGroup.Text>$</InputGroup.Text>
                                                                        <input
                                                                            type='text'
                                                                            {...register(`assets.${index}.opening_balance`)}
                                                                            placeholder={t("table.assets.input.amount.placeholder")}
                                                                            onChange={(e) => handleAssetsInputChange(e, index)}
                                                                            readOnly={!isEdit}
                                                                            className={`form-control opening-balance-amount-input ${isEdit ? '' : 'input-disabled-look'}`}
                                                                        />
                                                                    </InputGroup>
                                                                </td>
                                                            ) : <td className={getValues(`assets.${index}.account`) == 39990 ? 'text-end' : 'text-start'}>
                                                                <label htmlFor="">
                                                                    {
                                                                        getValues(`assets.${index}.account`) == 39990 ? <strong className="opening-balance-total-amount" style={{ fontSize: 'larger' }}>{'$' + formatNumber(getValues(`assets.${index}.opening_balance`))}</strong> : ''
                                                                    }
                                                                </label>
                                                            </td>
                                                        }
                                                    </tr>
                                                ))}
                                                {liabilitiesFields.map((item: any, index: number) => (
                                                    <tr key={item.id} className={`${['GH', 'SH', "SSH"].includes(item.report_code) ? 'grey-background' : ''} ${['GT', 'ST', 'SST'].includes(item.report_code) ? 'total-row' : ''}`}>
                                                        <td className='text-start'>
                                                            <label htmlFor="">
                                                                {
                                                                    ['VP', 'IP', "VP/IP"].includes(item.report_code) ?
                                                                        getValues(`liabilities.${index}.account`) : [59990, 51990].includes(+getValues(`liabilities.${index}.account`)) ? (<span style={{ fontSize: 'larger' }}>{getValues(`liabilities.${index}.account`)}</span>) : ['GT', 'ST', 'SST'].includes(item.report_code) ? (
                                                                            <span></span>
                                                                        ) : <span>{getValues(`liabilities.${index}.account`)}</span>
                                                                }
                                                            </label>
                                                        </td>
                                                        <td className='text-start'>
                                                            {
                                                                ['VP', 'IP', "VP/IP"].includes(item.report_code) ?
                                                                    <input
                                                                        type='text'
                                                                        {...register(`liabilities.${index}.name`)}
                                                                        placeholder={t("table.liabilities.input.name.placeholder")}
                                                                        readOnly={!isEdit}
                                                                        className={`form-control ${errors && errors.liabilities && errors.liabilities[index]?.name ? 'is-invalid' : ''} ${isEdit ? '' : 'input-disabled-look'}`}
                                                                    /> : <label htmlFor="">
                                                                        {
                                                                            [59990, 51990].includes(+getValues(`liabilities.${index}.account`)) ?
                                                                                <span style={{ fontSize: 'larger' }}>
                                                                                    {getValues(`liabilities.${index}.name`)}
                                                                                </span> :
                                                                                <span>
                                                                                    {getValues(`liabilities.${index}.name`)}
                                                                                </span>
                                                                        }
                                                                    </label>
                                                            }
                                                        </td>
                                                        {
                                                            ['VP', 'IP', "VP/IP"].includes(item.report_code) ? (
                                                                <td>
                                                                    <InputGroup hasValidation className="input-group-custom">
                                                                        <InputGroup.Text>$</InputGroup.Text>
                                                                        <input
                                                                            type='text'
                                                                            {...register(`liabilities.${index}.opening_balance`)}
                                                                            placeholder={t("table.liabilities.input.amount.placeholder")}
                                                                            onChange={(e) => handleLiabilitiesInputChange(e, index)}
                                                                            readOnly={!isEdit}
                                                                            className={`form-control opening-balance-amount-input ${isEdit ? '' : 'input-disabled-look'}`}
                                                                        />
                                                                    </InputGroup>
                                                                </td>
                                                            ) : <td className={[59990, 51990].includes(+getValues(`liabilities.${index}.account`)) ? 'text-end' : 'text-start'}>
                                                                <label htmlFor="">
                                                                    {
                                                                        [59990, 51990].includes(+getValues(`liabilities.${index}.account`)) ? <strong className="opening-balance-total-amount" style={{ fontSize: 'larger' }}>{'$' + formatNumber(getValues(`liabilities.${index}.opening_balance`))}</strong> : ''
                                                                    }
                                                                </label>
                                                            </td>
                                                        }
                                                    </tr>
                                                ))}
                                            </table>
                                        </td>
                                    </tr>
                                </table>
                            </div>
                            <button type="button" onClick={submitForm} className="btn btn-primary mt-4">{t("button.verifyAndSave")}</button>
                        </Form>
                        </div>
                    </div>
                )}
            {/* Submitting a beginning balance is a commit, not a destructive action, and it
                can now be changed afterwards — hence the primary colour rather than the
                danger red reserved for deletes, and no "cannot revert" warning. */}
            <SAlert
                show={showConfirm}
                icon={'warning'}
                showCancel={true}
                confirmBtnText={t("alert.confirm.confirmBtn")}
                cancelBtnText={t("alert.confirm.cancelBtn")}
                confirmBtnBsStyle={'primary'}
                cancelBtnBsStyle={"secondary"}
                title={t("alert.confirm.title", { date: moment(selectedFilter['endDate']).format('MMM, YYYY') })}
                onConfirm={() => {
                    handleSubmit(_handleSubmit)()
                    setShowConfirm(false);
                }}
                onCancel={() => setShowConfirm(false)}
            />
        </div>
        </div>
        </div>

    )
}

export default QBBeginningBalanceClassification;
